import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Lock as LockIcon, NotificationsActive as PushIcon, Info as InfoIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { getMyNotificationPreferences, updateMyNotificationPreferences, sendTestPush } from '../api';
import { isPushSupported, getExistingSubscription, enablePush, disablePush } from '../utils/pushClient';

export default function UserNotificationPreferences({ open, onClose }) {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const pushSupported = isPushSupported();

  useEffect(() => {
    if (open) {
      loadPrefs();
      if (isPushSupported()) {
        getExistingSubscription().then(sub => setPushEnabled(!!sub)).catch(() => {});
      }
    }
  }, [open]);

  const handlePushToggle = async () => {
    setPushBusy(true);
    setError('');
    setSuccess('');
    try {
      if (pushEnabled) {
        await disablePush();
        setPushEnabled(false);
        setSuccess('Push notifications disabled on this device');
      } else {
        await enablePush();
        setPushEnabled(true);
        setSuccess('Push notifications enabled on this device');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update push settings');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setPushBusy(true);
    setError('');
    try {
      const res = await sendTestPush();
      setSuccess(res.data.sent > 0
        ? 'Test notification sent — check this device'
        : 'No subscribed devices found — try re-enabling push');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test notification');
    } finally {
      setPushBusy(false);
    }
  };

  const loadPrefs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMyNotificationPreferences();
      setPreferences(res.data.preferences || []);
    } catch (err) {
      setError('Failed to load notification preferences');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (type, channel) => {
    setPreferences(prev =>
      prev.map(p => {
        if (p.type !== type || p.always_on) return p;
        return { ...p, [channel]: !p[channel] };
      })
    );
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateMyNotificationPreferences(
        preferences.map(p => ({
          type: p.type,
          email_enabled: p.email_enabled,
          in_app_enabled: p.in_app_enabled,
        }))
      );
      setSuccess('Preferences saved');
    } catch (err) {
      setError('Failed to save preferences');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notification Preferences</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Per-device web push (independent of the per-type toggles below) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, mb: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <PushIcon fontSize="small" color={pushEnabled ? 'primary' : 'disabled'} />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">Push notifications on this device</Typography>
              <Tooltip title="Sends browser/phone push alerts for the notification types enabled below, even when HourTrax isn't open. Each device is enabled separately. Requires installing HourTrax to your home screen on iPhone/iPad.">
                <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {!pushSupported
                ? 'Not supported in this browser'
                : pushEnabled ? 'Enabled on this device' : 'Off on this device'}
            </Typography>
          </Box>
          {pushSupported && pushEnabled && (
            <Button size="small" onClick={handleTestPush} disabled={pushBusy}>Test</Button>
          )}
          <Switch
            size="small"
            checked={pushEnabled}
            onChange={handlePushToggle}
            disabled={!pushSupported || pushBusy}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Column headers */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ width: 60, textAlign: 'center' }}>Email</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 60, textAlign: 'center' }}>In-App</Typography>
            </Box>
            <Divider />

            <List dense>
              {preferences.map((pref) => (
                <ListItem key={pref.type} sx={{ pr: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {pref.label}
                        {pref.always_on && <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      </Box>
                    }
                    secondary={pref.description}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={pref.email_enabled}
                    onChange={() => handleToggle(pref.type, 'email_enabled')}
                    disabled={pref.always_on}
                    sx={{ mx: 0.5 }}
                  />
                  <Switch
                    size="small"
                    checked={pref.in_app_enabled}
                    onChange={() => handleToggle(pref.type, 'in_app_enabled')}
                    disabled={pref.always_on}
                    sx={{ mx: 0.5 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
